'use client';
import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';

import debounce from 'lodash/debounce';
import { usePathname, useSearchParams } from 'next/navigation';

import { updateBoostedImpressionsAttributions } from './api';
import { getBoostedImpressionsAttributions } from './helpers';
import { type Components, type Impression, type PendingBatch } from './types';

import { type ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { getRouteView } from '@/modules/activityTracker/referers';
import {
  createTrackingId,
  useActivityTracker,
} from '@/modules/activityTracker/useActivityTracker';
import {
  calculateIntersectionObserverRootMargin,
  getBrowserVisibilityProp,
  getIsDocumentHidden,
} from '@/modules/dom/helpers';
import { DEPOP_FONT } from '@/modules/fonts/helpers';
import { getLogger } from '@/modules/observability/logging';
import { ROUTE_CHANGE_START_EVENT } from '@/modules/routing/constants';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@/modules/storage';
import { safeJsonParse } from '@/modules/storage/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';
import { useStateRef } from '@/utils/useStateRef';

export const RESIZE_DEBOUNCE_MS = 16;

/**
 * Max send attempts for a batch before it's dropped. Bounds retry volume (and
 * the persisted pending queue) during a sustained backend outage.
 */
export const MAX_SEND_ATTEMPTS = 3;

export const ImpressionsBatcherContext = React.createContext<{
  impressions: Impression[];
  setImpressions: Dispatch<SetStateAction<Impression[]>>;
  rootMargin?: string;
}>({
  impressions: [],
  setImpressions: () => null,
  rootMargin: undefined,
});

export function ImpressionsBatcherProvider({
  batchLocationId,
  impressionsType,
  eventType,
  batchLimit = 10,
  children,
  depopSearchId,
  component,
}: {
  batchLocationId: string;
  children: React.ReactNode;
  eventType: ActivityTrackerEventType;
  impressionsType: string;
  batchLimit?: number;
  depopSearchId?: string;
  component?: Components;
}) {
  const LOCAL_STORAGE_IMPRESSIONS_KEY = `${batchLocationId}_${impressionsType}`;
  const LOCAL_STORAGE_BOOSTED_IMPRESSIONS_ATTRIBUTIONS_KEY = `${batchLocationId}_${impressionsType}_attributions`;
  const LOCAL_STORAGE_IMPRESSIONS_DEPOP_SEARCH_ID_KEY = `${batchLocationId}_depopSearchId`;
  const LOCAL_STORAGE_PENDING_BATCHES_KEY = `${batchLocationId}_${impressionsType}_pending`;

  const [_, currentUser] = useCurrentUser();
  const [rootMargin, setRootMargin] = useState<string>();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  /**
   * The event listener callbacks defined in the useEffects are unable to access the latest state values when invoked
   * Need to tie state to ref instead
   * */
  const {
    ref: isEventSendingRef,
    value: isEventSending,
    setValue: setIsEventSending,
  } = useStateRef(false);
  const {
    ref: impressionsRef,
    setValue: setImpressions,
    value: impressions,
  } = useStateRef<Impression[]>([]);
  const { ref: depopSearchIdRef, setValue: setDepopSearchId } =
    useStateRef(depopSearchId);
  const { ref: failedBatchesRef, setValue: setFailedBatches } = useStateRef<
    PendingBatch[]
  >([]);
  const path = searchParams?.toString()
    ? `${pathname}?${searchParams}`
    : pathname;
  const pageType = getRouteView(path);
  const impressionsCount = impressions.length;

  function resetImpressions() {
    setImpressions([]);
  }

  /** This is required so long as adding .catch to sendActivityTrackerEvent does nothing... */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  function handleRequestFailure(eventData?: Record<string, any>) {
    const interimImpressions = impressionsRef?.current || [];
    if (eventData?.productImpressions) {
      // Restore impressions if the request fails, filtering out duplicates based on timestamp AND product ID
      const existingKeys = new Set(
        interimImpressions.map(
          (imp) => `${imp.impressionTime}-${imp.productId}`
        )
      );
      const newImpressions = eventData.productImpressions.filter(
        (impression: Impression) =>
          !existingKeys.has(
            `${impression.impressionTime}-${impression.productId}`
          )
      );
      const impressionsToRestore = interimImpressions.concat(newImpressions);

      setImpressions(impressionsToRestore);
      setIsEventSending(false);
    }
  }

  function sendBoostedImpressionsAttributions(impressions: Impression[]) {
    const boostedAttributions = getBoostedImpressionsAttributions(impressions);

    if (!boostedAttributions.length) {
      return Promise.resolve();
    }

    return updateBoostedImpressionsAttributions(boostedAttributions).catch(
      () => {
        handleRequestFailure(impressions);
      }
    );
  }

  function enqueueFailedBatch(batch: PendingBatch) {
    setFailedBatches((previous) => [...previous, batch]);
  }

  function sendAllImpressionsToAT({
    impressions,
    searchId,
    batchId,
    attempt,
  }: {
    impressions: Impression[];
    searchId?: string | null;
    batchId: string;
    attempt: number;
  }) {
    return sendActivityTrackerEvent(
      eventType,
      {
        [impressionsType]: impressions.map((impression) => ({
          ...impression,
          ...(component && {
            component,
          }),
        })),
        schemaVersion: '4.0',
        ...(searchId && {
          depopSearchId: searchId,
        }),
        page: pageType,
      },
      {
        baseEventOverrides: { id: batchId },
        // On failure, re-queue the batch under its original id so the retry
        // reuses the same `baseEvent.id` for backend dedup. Interim impressions
        // are never merged in — they get their own event with a new id. Drop
        // after MAX_SEND_ATTEMPTS so a payload the backend keeps rejecting can't
        // retry (and re-persist) forever.
        handleRequestFailure: () => {
          if (attempt >= MAX_SEND_ATTEMPTS) {
            getLogger().warn(
              'Dropping impressions batch after max send attempts',
              {
                batchId,
                attempts: attempt,
                impressionsCount: impressions.length,
              }
            );
            return;
          }
          enqueueFailedBatch({
            id: batchId,
            impressions,
            searchId,
            attempts: attempt,
          });
        },
      }
    );
  }

  async function sendBatchEvent() {
    if (isEventSendingRef?.current) {
      return;
    }

    const pendingFailedBatches = failedBatchesRef?.current ?? [];
    const impressions = impressionsRef?.current ?? [];

    if (!pendingFailedBatches.length && !impressions.length) {
      return;
    }

    // Clear state before sending so new impressions can collect while these
    // requests are in flight.
    resetImpressions();
    if (pendingFailedBatches.length) {
      setFailedBatches([]);
    }
    setIsEventSending(true);

    const requests: Array<Promise<unknown>> = [];

    // Retry each failed batch verbatim, reusing its original id.
    pendingFailedBatches.forEach((batch) => {
      requests.push(
        sendAllImpressionsToAT({
          impressions: batch.impressions,
          searchId: batch.searchId,
          batchId: batch.id,
          attempt: (batch.attempts ?? 0) + 1,
        }),
        sendBoostedImpressionsAttributions(batch.impressions)
      );
    });

    // Send freshly-collected impressions as their own event with a new id.
    if (impressions.length) {
      requests.push(
        sendAllImpressionsToAT({
          impressions,
          searchId: depopSearchIdRef?.current,
          batchId: createTrackingId(),
          attempt: 1,
        }),
        sendBoostedImpressionsAttributions(impressions)
      );
    }

    await Promise.all(requests);

    setIsEventSending(false);
  }

  function onEventChange(e: Event, condition = true) {
    if (e.stopImmediatePropagation) {
      e.stopImmediatePropagation();
    }

    if (condition) {
      sendBatchEvent();
    }
  }

  async function restoreBoostedImpressions() {
    const restoredBoostedImpressions = getLocalStorageItem(
      LOCAL_STORAGE_BOOSTED_IMPRESSIONS_ATTRIBUTIONS_KEY
    );

    if (restoredBoostedImpressions) {
      try {
        const parsedImpressions = safeJsonParse(restoredBoostedImpressions, []);
        if (parsedImpressions.length) {
          await sendBoostedImpressionsAttributions(parsedImpressions).then(
            () => {
              removeLocalStorageItem(
                LOCAL_STORAGE_BOOSTED_IMPRESSIONS_ATTRIBUTIONS_KEY
              );
            }
          );
        }
      } catch (error) {
        getLogger().error(
          `Failed to parse impressions JSON data for local storage key: ${LOCAL_STORAGE_BOOSTED_IMPRESSIONS_ATTRIBUTIONS_KEY}`,
          { error }
        );
      }
    }
  }

  async function restoreImpressions() {
    const restoredImpressions = getLocalStorageItem(
      LOCAL_STORAGE_IMPRESSIONS_KEY
    );
    const restoredDepopSearchId = getLocalStorageItem(
      LOCAL_STORAGE_IMPRESSIONS_DEPOP_SEARCH_ID_KEY
    );

    if (restoredImpressions) {
      try {
        const parsedImpressions = safeJsonParse(restoredImpressions, []);
        if (parsedImpressions.length) {
          await sendAllImpressionsToAT({
            impressions: parsedImpressions,
            searchId: restoredDepopSearchId,
            batchId: createTrackingId(),
            attempt: 1,
          }).then(() => {
            removeLocalStorageItem(LOCAL_STORAGE_IMPRESSIONS_KEY);
            removeLocalStorageItem(
              LOCAL_STORAGE_IMPRESSIONS_DEPOP_SEARCH_ID_KEY
            );
          });
        }
      } catch (error) {
        getLogger().error(
          `Failed to parse impressions JSON data for local storage key: ${LOCAL_STORAGE_IMPRESSIONS_KEY}`,
          { error }
        );
      }
    }
  }

  async function restorePendingBatches() {
    const restoredPendingBatches = getLocalStorageItem(
      LOCAL_STORAGE_PENDING_BATCHES_KEY
    );

    if (restoredPendingBatches) {
      try {
        const parsedBatches: PendingBatch[] = safeJsonParse(
          restoredPendingBatches,
          []
        );
        if (parsedBatches.length) {
          // Resend both the AT event (reusing the original id) and boosted
          // attributions per batch, matching sendBatchEvent.
          await Promise.all(
            parsedBatches.flatMap((batch) => [
              sendAllImpressionsToAT({
                impressions: batch.impressions,
                searchId: batch.searchId,
                batchId: batch.id,
                attempt: (batch.attempts ?? 0) + 1,
              }),
              sendBoostedImpressionsAttributions(batch.impressions),
            ])
          ).then(() => {
            removeLocalStorageItem(LOCAL_STORAGE_PENDING_BATCHES_KEY);
          });
        }
      } catch (error) {
        getLogger().error(
          `Failed to parse pending batches JSON data for local storage key: ${LOCAL_STORAGE_PENDING_BATCHES_KEY}`,
          { error }
        );
      }
    }
  }

  async function onReconnect() {
    setIsEventSending(true);
    await restoreBoostedImpressions();
    await restorePendingBatches();
    await restoreImpressions();
    setIsEventSending(false);
  }

  function onDisconnect() {
    setLocalStorageItem(
      LOCAL_STORAGE_IMPRESSIONS_KEY,
      JSON.stringify(impressionsRef?.current)
    );
    setLocalStorageItem(
      LOCAL_STORAGE_BOOSTED_IMPRESSIONS_ATTRIBUTIONS_KEY,
      JSON.stringify(impressionsRef?.current)
    );
    if (failedBatchesRef?.current.length) {
      setLocalStorageItem(
        LOCAL_STORAGE_PENDING_BATCHES_KEY,
        JSON.stringify(failedBatchesRef.current)
      );
      setFailedBatches([]);
    }
    if (depopSearchIdRef?.current) {
      setLocalStorageItem(
        LOCAL_STORAGE_IMPRESSIONS_DEPOP_SEARCH_ID_KEY,
        depopSearchIdRef?.current
      );
    }

    resetImpressions();
  }

  function handleRootMarginCalculation(e?: Event) {
    if (e?.stopImmediatePropagation) {
      e.stopImmediatePropagation();
    }

    const calculatedRootMargin = calculateIntersectionObserverRootMargin();
    setRootMargin(calculatedRootMargin);
  }

  function handleVisibilityChange(e: Event) {
    return onEventChange(e, getIsDocumentHidden());
  }

  useEffect(() => {
    restoreImpressions();
    restoreBoostedImpressions();
    restorePendingBatches();
    /**
     * If client-side render, fonts will have loaded.
     * Therefore, we do not need to wait for fontLoadingCompleted event to calculate the rootMargin
     */
    if (document?.documentElement.classList.contains(DEPOP_FONT)) {
      handleRootMarginCalculation();
    }

    const visibilityChange = getBrowserVisibilityProp();

    /** USER EVENTS */
    // Network handling
    window.addEventListener('offline', onDisconnect);
    window.addEventListener('online', onReconnect);
    // User changes route
    window.addEventListener(ROUTE_CHANGE_START_EVENT, onEventChange);
    // User changes tab or minimises window
    window.addEventListener(visibilityChange, handleVisibilityChange);
    // User closes tab, window
    window.addEventListener('beforeunload', onDisconnect);

    // User resizes the window
    window.addEventListener(
      'resize',
      debounce(handleRootMarginCalculation, RESIZE_DEBOUNCE_MS)
    );

    /** WEBSITE EVENTS */
    window.addEventListener(
      'fontLoadingCompleted',
      handleRootMarginCalculation
    );

    return () => {
      window.removeEventListener(ROUTE_CHANGE_START_EVENT, onEventChange);
      window.removeEventListener('beforeunload', onDisconnect);
      window.removeEventListener(visibilityChange, handleVisibilityChange);
      window.removeEventListener('offline', onDisconnect);
      window.removeEventListener('online', onReconnect);
      window.removeEventListener(
        'resize',
        debounce(handleRootMarginCalculation, RESIZE_DEBOUNCE_MS)
      );
      window.removeEventListener(
        'fontLoadingCompleted',
        handleRootMarginCalculation
      );
    };
  }, []);

  useEffect(() => {
    /**
     * The depopSearchId is the main search term for a group of impressions.
     * It exists at the top level of an event, and at the individual impression level.
     * As such, when it changes, we should send the batch of events for the previous search term.
     */
    sendBatchEvent();
    setDepopSearchId(depopSearchId);
  }, [depopSearchId]);

  useEffect(() => {
    // Auto-send only when freshly-collected impressions reach the batch limit.
    // Failed batches are retried on natural flushes (route change, tab hidden,
    // reconnect, unload) rather than on every failure, so a sustained backend
    // outage can't turn the growing pending queue into a per-round-trip resend
    // storm.
    if (!isEventSending && impressionsRef?.current.length >= batchLimit) {
      sendBatchEvent();
    }
  }, [impressionsCount, isEventSending]);

  return (
    <ImpressionsBatcherContext.Provider
      value={{
        impressions: impressionsRef?.current,
        setImpressions,
        rootMargin,
      }}
    >
      {children}
    </ImpressionsBatcherContext.Provider>
  );
}
